/**
 * Campaign create / edit — writes through Surface B (`management.campaigns`). Mirrors the legacy
 * rtb_ui form, including the INVERTED "Disallow open exchange" checkbox (checked ⇒ allow_open_exchange 'N').
 */

import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { management, hierarchy, ApiError } from '../lib/api';
import { ClientPicker } from '../components/ClientPicker';
import { IabTree } from '../components/IabTree';
import { IAB_CATEGORIES } from '../lib/iab-categories';

/** Every IAB category id — the "target everything" set used when strict matching is OFF. */
const ALL_IAB_IDS = IAB_CATEGORIES.map((c) => c.id);

type Form = Record<string, unknown>;
const str = (v: unknown) => (v === null || v === undefined ? '' : String(v));

export function CampaignFormScreen(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const editing = Boolean(id);
  const navigate = useNavigate();
  const [f, setF] = useState<Form>({
    // Strict matching defaults OFF, so a new campaign targets ALL verticals.
    client_id: '', client_company: '', so: '', campaign_name: '', vertical_ids: ALL_IAB_IDS, strict_iab_matching: false,
    overall_budget: '', daily_budget: '', delivery_type: 'STANDARD', retargeting_frequency: '10',
    private_deal_id: '', disallow_open_exchange: false, deal_bid_amount: '0', buyer_seat: '', is_active: true,
  });
  const [loading, setLoading] = useState(editing);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const set = (k: string, v: unknown) => setF((p) => ({ ...p, [k]: v }));

  useEffect(() => {
    if (!editing || !id) return;
    let live = true;
    management.campaigns.get(id).then(async ({ campaign: c }) => {
      if (!live) return;
      const strict = c.strict_iab_matching === 'Y';
      const stored = Array.isArray(c.vertical) ? (c.vertical as unknown[]).map(String) : str(c.vertical).split(',').map((s) => s.trim()).filter(Boolean);
      // Non-strict campaigns target everything, so reflect that as all-checked.
      const verts = strict ? stored : ALL_IAB_IDS;
      let company = '';
      if (c.client_id) { try { company = (await hierarchy.client(str(c.client_id))).companyName ?? ''; } catch { /* client may be gone */ } }
      if (!live) return;
      setF({
        client_id: str(c.client_id), client_company: company, so: str(c.so), campaign_name: str(c.campaign_name),
        vertical_ids: verts, strict_iab_matching: strict,
        overall_budget: str(c.overall_budget), daily_budget: str(c.daily_budget),
        delivery_type: str(c.delivery_type) || 'STANDARD', retargeting_frequency: str(c.retargeting_frequency),
        private_deal_id: str(c.private_deal_id), disallow_open_exchange: c.allow_open_exchange === 'N',
        deal_bid_amount: str(c.deal_bid_amount), buyer_seat: str(c.buyer_seat), is_active: c.is_active === 'Y',
      });
      setLoading(false);
    }).catch((e) => { setError(e instanceof ApiError ? e.message : 'Could not load the campaign.'); setLoading(false); });
    return () => { live = false; };
  }, [editing, id]);

  const submit = useCallback(async () => {
    setError(null);
    if (!f.client_id) { setError('Choose a client.'); return; }
    if (!f.so || !f.campaign_name) { setError('SO # and campaign name are required.'); return; }
    if (f.strict_iab_matching && (f.vertical_ids as string[]).length === 0) {
      setError('Strict IAB matching is on — select at least one IAB vertical.'); return;
    }
    if (Number(f.daily_budget) > Number(f.overall_budget)) { setError('Daily budget must be ≤ overall budget.'); return; }
    setSaving(true);
    const body: Form = {
      client_id: f.client_id, io: f.so, so: f.so, campaign_name: f.campaign_name, // IO mirrors SO (non-email campaigns)
      vertical: (f.vertical_ids as string[]),
      strict_iab_matching: f.strict_iab_matching ? 'Y' : 'N',
      overall_budget: f.overall_budget, daily_budget: f.daily_budget, delivery_type: f.delivery_type,
      retargeting_frequency: f.retargeting_frequency, private_deal_id: f.private_deal_id,
      allow_open_exchange: f.disallow_open_exchange ? 'N' : 'Y', // inverted checkbox
      deal_bid_amount: f.deal_bid_amount, buyer_seat: f.buyer_seat, is_active: f.is_active ? 'Y' : 'N',
    };
    try {
      if (editing && id) { await management.campaigns.update(id, body); navigate(`/campaigns/${id}`); }
      else { const { campaign } = await management.campaigns.create(body); navigate(`/campaigns/${String(campaign.id)}`); }
    } catch (e) { setError(e instanceof ApiError ? e.message : 'Save failed.'); setSaving(false); }
  }, [f, editing, id, navigate]);

  if (loading) return <div className="busy">Loading…</div>;

  return (
    <div className="mform">
      <h1>{editing ? 'Edit campaign' : 'New campaign'}</h1>
      <p className="sub">Campaigns write to the bidder serving path — {editing ? 'edits apply on the next loader run.' : 'a new campaign is inactive until you add an ad group and creatives.'}</p>
      {error && <div className="banner error" style={{ marginBottom: '1rem' }}>{error}</div>}

      <section>
        <h2>Identity</h2>
        <div className="mgrid">
          <ClientPicker
            clientId={str(f.client_id)} companyName={str(f.client_company)}
            onChange={(cid, name) => setF((p) => ({ ...p, client_id: cid, client_company: name }))}
          />
          <Text f={f} set={set} k="so" label="SO #" full hint="The order number. The IO number is saved to match it." />
          <Text f={f} set={set} k="campaign_name" label="Campaign name" full />
          <div className="field full check">
            <input
              id="strict_iab_matching" type="checkbox" checked={Boolean(f.strict_iab_matching)}
              onChange={(e) => {
                const on = e.target.checked;
                // Strict ON → clear the tree (pick specific, ≥1 required). OFF → target everything.
                setF((p) => ({ ...p, strict_iab_matching: on, vertical_ids: on ? [] : ALL_IAB_IDS }));
              }}
            />
            <label htmlFor="strict_iab_matching">Strict IAB matching</label>
          </div>
          <div className="field full">
            <label>IAB verticals</label>
            <IabTree
              value={f.vertical_ids as string[]} disabled={!f.strict_iab_matching}
              onChange={(ids) => setF((p) => ({ ...p, vertical_ids: ids }))}
            />
            <span className="hint">
              {f.strict_iab_matching
                ? 'Strict matching — choose the specific verticals to target (at least one required).'
                : 'Not strict — every vertical is targeted. Turn on strict matching to choose specific ones.'}
            </span>
          </div>
        </div>
      </section>

      <section>
        <h2>Budget &amp; delivery</h2>
        <div className="mgrid">
          <Text f={f} set={set} k="overall_budget" label="Overall budget" type="number" />
          <Text f={f} set={set} k="daily_budget" label="Daily budget" type="number" hint="Must be ≤ overall." />
          <Select f={f} set={set} k="delivery_type" label="Delivery type" options={['STANDARD', 'ACCELERATED']} />
          <Text f={f} set={set} k="retargeting_frequency" label="Retargeting frequency" type="number" />
          <Text f={f} set={set} k="deal_bid_amount" label="Deal bid amount" type="number" />
          <Text f={f} set={set} k="private_deal_id" label="Private deal ID" />
          <Text f={f} set={set} k="buyer_seat" label="Buyer seat" />
          <Check f={f} set={set} k="disallow_open_exchange" label="Disallow open exchange" />
          <Check f={f} set={set} k="is_active" label="Active" />
        </div>
      </section>

      <div className="mform-actions">
        <button className="button primary" onClick={submit} disabled={saving}>{saving ? 'Saving…' : editing ? 'Save changes' : 'Create campaign'}</button>
        <button className="button" onClick={() => navigate(editing && id ? `/campaigns/${id}` : '/campaigns')}>Cancel</button>
      </div>
    </div>
  );
}

// ── small field primitives (shared by all three management forms via re-export) ──
export function Text({ f, set, k, label, hint, type = 'text', full }: { f: Form; set: (k: string, v: unknown) => void; k: string; label: string; hint?: string; type?: string; full?: boolean }): JSX.Element {
  return (
    <div className={`field${full ? ' full' : ''}`}>
      <label htmlFor={k}>{label}</label>
      <input id={k} type={type} value={str(f[k])} onChange={(e) => set(k, e.target.value)} />
      {hint && <span className="hint">{hint}</span>}
    </div>
  );
}
export function Select({ f, set, k, label, options, full }: { f: Form; set: (k: string, v: unknown) => void; k: string; label: string; options: string[]; full?: boolean }): JSX.Element {
  return (
    <div className={`field${full ? ' full' : ''}`}>
      <label htmlFor={k}>{label}</label>
      <select id={k} value={str(f[k])} onChange={(e) => set(k, e.target.value)}>
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
}
export function Check({ f, set, k, label }: { f: Form; set: (k: string, v: unknown) => void; k: string; label: string }): JSX.Element {
  return (
    <div className="field check">
      <input id={k} type="checkbox" checked={Boolean(f[k])} onChange={(e) => set(k, e.target.checked)} />
      <label htmlFor={k}>{label}</label>
    </div>
  );
}
